import {useCallback, useEffect, useState} from "react";
import {RefreshCw, Sparkles} from "lucide-react";

const CHECK_INTERVAL_MS = 60 * 1000;
const INITIAL_CHECK_DELAY_MS = 12 * 1000;

function getCurrentAssetSignature() {
  const assets = Array.from(
    document.querySelectorAll("script[src], link[rel='stylesheet'][href]")
  )
    .map((item) => item.src || item.href)
    .map((url) => new URL(url, window.location.origin).pathname)
    .filter((pathname) =>
      /\/static\/(?:js|css)\/main(?:\.[^/]+)?\.(?:js|css)$/.test(pathname)
    )
    .sort();

  return assets.join("|");
}

function getPublishedAssetSignature(manifest) {
  const publishedAssets = Array.from(
    new Set([
      ...(manifest?.entrypoints || []),
      ...Object.values(manifest?.files || {}),
    ])
  );

  return publishedAssets
    .map((entrypoint) => new URL(entrypoint, window.location.origin).pathname)
    .filter((pathname) =>
      /\/static\/(?:js|css)\/main(?:\.[^/]+)?\.(?:js|css)$/.test(pathname)
    )
    .filter((pathname, index, paths) => paths.indexOf(pathname) === index)
    .sort()
    .join("|");
}

export default function AppUpdateNotice() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [reloading, setReloading] = useState(false);

  const checkForUpdate = useCallback(async () => {
    const currentBundle = getCurrentAssetSignature();
    if (!currentBundle) return;

    try {
      const response = await fetch(
        `/asset-manifest.json?vd_update_check=${Date.now()}`,
        {cache: "no-store"}
      );

      if (!response.ok) return;

      const manifest = await response.json();
      const publishedBundle = getPublishedAssetSignature(manifest);

      if (publishedBundle && publishedBundle !== currentBundle) {
        setUpdateAvailable(true);
      }
    } catch {
      // El aviso no debe interrumpir el uso de la app si el check falla.
    }
  }, []);

  useEffect(() => {
    const initialTimer = window.setTimeout(checkForUpdate, INITIAL_CHECK_DELAY_MS);
    const interval = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkForUpdate]);

  if (!updateAvailable) return null;

  const reloadApp = () => {
    setReloading(true);
    window.location.reload();
  };

  return (
    <aside className="vd-update-notice" role="status" aria-live="polite">
      <div className="vd-update-notice__icon" aria-hidden="true">
        <Sparkles size={19} strokeWidth={2.2} />
      </div>
      <div className="vd-update-notice__content">
        <strong>VexaDent tiene actualizaciones</strong>
        <span>Recarga la app para usar las mejoras más recientes.</span>
      </div>
      <button
        className="vd-update-notice__button"
        type="button"
        onClick={reloadApp}
        disabled={reloading}
      >
        <RefreshCw size={16} className={reloading ? "is-spinning" : ""} />
        {reloading ? "Recargando…" : "Recargar"}
      </button>
    </aside>
  );
}
